import React, { Suspense, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Redirect, Route, Switch, withRouter, useHistory,
} from 'react-router-dom';
import { connect } from 'react-redux';
import { Container } from 'reactstrap';
import { Flip, ToastContainer } from 'react-toastify';

import { Helmet } from 'react-helmet';
import MainMenu from './components/MainMenu';
import SecureRoute from '../SecureRoute/SecureRoute';
import { getUserSession } from '../../auth/actions';
import { getTranslations } from '../../i18n/actions';
import { getIssuerSettings } from './actions';
import SecuritizePage from '../../components/SecuritizePage/SecuritizePage';

// routes config
import routes from '../../routes';
import ModalWrapper from '../../components/ModalComponent/ModalWrapper';
import LanguageSelect from '../../components/LanguageSelect/LanguageSelect';
import Pendo from './components/Pendo';

const issuerOverrideStyles = require('../../assets/issuer-styles.template.css').toString();

const containerStyle = { zIndex: 1999 };

const Loading = () => (
  <div className="loader animated fadeIn pt-1 text-center">
    <div className="sk-spinner sk-spinner-pulse" />
  </div>
);

const DefaultLayout = (props) => {
  const {
    token,
    getUserSession,
    getTranslations,
    activeLanguage,
    getIssuerSettings,
    isLoading,
    isTranslationError,
    isAuthenticated,
    location: { pathname },
    issuerConfig,
    user,
    isError,
  } = props;
  const history = useHistory();
  const isExternalAccreditation = user && user.accreditationType === 'third-party-accreditation';
  const isAccreditationFirst = (() => {
    if (isAuthenticated && user && user.accreditationFirst) {
      return isExternalAccreditation ? !user.isAccredited || !user.isAdditionalAccredited : !user.isAccredited;
    }
    return false;
  })();

  useEffect(() => {
    if (user && user.isPendingApproval) {
      history.push('/pending');
    }
  }, [user && user.isPendingApproval]);

  useEffect(() => {
    if (user && user.isRestricted) {
      history.push('/not-authorized');
    }
  }, [user && user.isRestricted]);

  useEffect(() => {
    if (!(user && user.isPendingApproval) && isAccreditationFirst) {
      if (isExternalAccreditation && user.isAccredited && pathname !== 'verify-profile') {
        history.push('/verify-profile');
      } else if (pathname !== 'investor-qualification') {
        history.push('/investor-qualification');
      }
    }
  }, [isAccreditationFirst, pathname]);

  useEffect(() => {
    if (token) {
      getUserSession(
        /* (error, data) => {
          getTranslations(error ? activeLanguage : data.user.language);
        }, */
      );
    }
    getIssuerSettings(({ config: { issuerColor, issuerLanguage } }) => {
      // replace issuer color in the css template and append
      const style = document.createElement('style');
      style.textContent = issuerOverrideStyles.replace(/\$primary-color/gi, issuerColor || '#fc0017');
      document.body.append(style);
      getTranslations(issuerLanguage || activeLanguage);
    });
  }, []);

  const isAuthorize = pathname === '/authorize';
  const is404Page = pathname === '/404';
  return (
    <>
      <Helmet>
        <title>{isLoading ? 'Investment Platform' : issuerConfig.issuerName }</title>
        <link rel="icon" type="image/png" href={issuerConfig.favicon} />
      </Helmet>
      <SecuritizePage isLoading={isLoading} isError={isError} isTranslationError={isTranslationError}>
        <Pendo />
        <div className="app securitize-app">
          <ToastContainer
            transition={Flip}
            position="bottom-right"
            autoClose={5000}
            style={containerStyle}
          />
          <ModalWrapper />
          {isAuthenticated && user && !isAccreditationFirst && !is404Page && (
            <MainMenu
              pathname={pathname}
              authenticated={!isAuthorize}
              issuerLogo={issuerConfig.logoSquare}
            />
          )}
          <div className="app-body securitize-app__body">
            <main className="main">
              <Container fluid>
                {
                  ((!isAuthenticated || isAuthorize || isAccreditationFirst) && !is404Page)
                    && (
                    <div className="d-flex align-items-center justify-content-between animated fadeIn pb-4 mt-0">
                      <img src={issuerConfig.logo} className="securitize-app__header__issuer-logo " alt="" />
                      <LanguageSelect />
                    </div>
                    )
                }
                <Suspense fallback={<Loading />}>
                  <Switch>
                    {routes.map((route) => (route.component ? (
                      route.secure === false ? (
                        <Route
                          key={route.path}
                          path={route.path}
                          exact={route.exact}
                          name={route.name}
                          component={(props) => (
                            <route.component {...props} /> // eslint-disable-line
                          )}
                        />
                      ) : (
                        <SecureRoute
                          key={route.path}
                          path={route.path}
                          exact={route.exact}
                          name={route.name}
                          component={(props) => (
                            <route.component {...props} /> // eslint-disable-line
                          )}
                        />
                      )
                    ) : null))}
                    <Redirect
                      from="/"
                      to={isAuthenticated ? '/home' : '/login'}
                    />
                  </Switch>
                </Suspense>
              </Container>
            </main>
          </div>
        </div>
      </SecuritizePage>
    </>
  );
};

DefaultLayout.propTypes = {
  getUserSession: PropTypes.func,
  getTranslations: PropTypes.func,
  getIssuerSettings: PropTypes.func,
  activeLanguage: PropTypes.string,
  token: PropTypes.string,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }),
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  isAuthenticated: PropTypes.bool,
  isTranslationError: PropTypes.bool,
  issuerConfig: PropTypes.objectOf(PropTypes.any).isRequired,
  user: PropTypes.objectOf(PropTypes.any),
};

DefaultLayout.defaultProps = {
  user: null,
  token: null,
  isLoading: true,
  isAuthenticated: false,
  isTranslationError: false,
  isError: false,
  getIssuerSettings: () => undefined,
  getUserSession: () => undefined,
  getTranslations: () => undefined,
  activeLanguage: 'EN',
  location: {
    pathname: '/',
  },
};

const mapStateToProps = ({ auth, i18n, issuerConfiguration }) => ({
  activeLanguage: i18n.activeLanguage,
  isLoading: auth.isLoading || i18n.isLoading || issuerConfiguration.isLoading,
  token: auth.token,
  isAuthenticated: auth.isAuthenticated,
  user: auth.user,
  issuerConfig: issuerConfiguration.config,
  isError: issuerConfiguration.isError,
  isTranslationError: i18n.isError,
});

export default connect(
  mapStateToProps,
  {
    getUserSession,
    getTranslations,
    getIssuerSettings,
  },
)(withRouter(DefaultLayout));
